import React, { useState } from 'react'
import { Button } from './ui/Button'
import { Card, CardContent } from './ui/Card'
import {X, ChevronRight, ChevronLeft, UserPlus, Search, Heart} from 'lucide-react'

interface Step {
  icon: React.ReactNode
  title: string
  description: string
  visual: string
}

const TUTORIAL_STEPS: Step[] = [
  {
    icon: <UserPlus className="h-12 w-12 text-red-600" />,
    title: 'Cadastre-se em 30 segundos',
    description: 'Nome, tipo sanguíneo e localização. Pronto! Você já faz parte da rede.',
    visual: '👤'
  },
  {
    icon: <Search className="h-12 w-12 text-red-600" />,
    title: 'Encontre pedidos próximos',
    description: 'Veja quem precisa de ajuda perto de você. Filtre por tipo e urgência.',
    visual: '📍'
  },
  {
    icon: <Heart className="h-12 w-12 text-red-600" />,
    title: 'Doe e ganhe reconhecimento',
    description: 'Confirme sua doação, ganhe pontos, medalhas e entre no ranking de heróis!',
    visual: '🏆'
  }
]

interface OnboardingTutorialProps {
  onComplete: () => void
  onSkip: () => void
}

export function OnboardingTutorial({ onComplete, onSkip }: OnboardingTutorialProps) {
  const [currentStep, setCurrentStep] = useState(0)

  const handleNext = () => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      onComplete()
    }
  }

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const step = TUTORIAL_STEPS[currentStep]

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-lg w-full relative bg-white">
        <button
          onClick={onSkip}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        <CardContent className="pt-12 pb-8 px-8">
          {/* Visual Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 rounded-full bg-red-50 flex items-center justify-center">
              {step.icon}
            </div>
          </div>

          {/* Step Counter */}
          <div className="flex justify-center gap-2 mb-6">
            {TUTORIAL_STEPS.map((_, index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all ${
                  index === currentStep
                    ? 'w-8 bg-red-600'
                    : 'w-2 bg-gray-300'
                }`}
              />
            ))}
          </div>

          {/* Content */}
          <div className="text-center space-y-4 mb-8">
            <div className="text-6xl mb-4">{step.visual}</div>
            <h2 className="text-2xl font-bold text-gray-900">{step.title}</h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              {step.description}
            </p>
          </div>

          {/* Navigation */}
          <div className="flex gap-3">
            {currentStep > 0 && (
              <Button
                variant="outline"
                onClick={handlePrev}
                className="flex-1"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
            )}
            <Button
              onClick={handleNext}
              className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
            >
              {currentStep === TUTORIAL_STEPS.length - 1 ? 'Começar' : 'Próximo'}
              {currentStep < TUTORIAL_STEPS.length - 1 && (
                <ChevronRight className="h-4 w-4 ml-2" />
              )}
            </Button>
          </div>

          {/* Skip Link */}
          <div className="text-center mt-4">
            <button
              onClick={onSkip}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Pular tutorial
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
